import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import {
  setPropertyUWMapState,
  setSelectedContainerId,
  fetchLastCompletedValuation
} from 'store/actions';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { format } from 'date-fns';
import {
  Typography,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ErrorBoundary from 'components/hoc/ErrorBoundary';
import {
  PropertyUWHeader,
  PropertyUWMap,
  PropertyUWGrid,
  PropertyUWDiscrepantData,
  PropertyUWLastCompleted,
  PropertyUWInvestmentManagementPricingNotes
} from 'components/underwrite/acquisition/propertyUnderwrite';
import {
  UnderwriteNotes,
  UnderwritePictures,
  UnderwriteWarningBanner
} from 'components/underwrite/shared';
import debounce from 'lodash/debounce';
import { MapWrapper } from 'components/map';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';
import PropertyHistoryTabs from './header/propertyHistory/PropertyHistoryTabs';

const PropertyUWContent = ({ classes, displayMode, selectedOpportunity,
  zoom, center, gestureHandling, mapViewType,
  setPropertyUWMapState, isAmherstManaged, selectedOpportunityHistory, selectedOpportunityEventHistory, selectedOpportunityValuationHistory,
  viewingReadonlyMode, snapshotDate, setSelectedContainerId, fetchLastCompletedValuation
}) => {
  const [isViewInspectionDialogOpen, setIsViewInspectionDialogOpen] = useState(false);
  const [key, setKey] = useState(new Date().toString());

  const onZoomChanged = debounce((map) => {
    if (!map) return;
    setPropertyUWMapState({ zoom: map.getZoom() });        // we store the zoom so that when we navigate away and navigate back, that we can set it back to the original zoom
  }, 900);
  const onCenterChanged = debounce((map) => {
    if (!map) return;
    const getCenterFunc = map.getCenter();
    setPropertyUWMapState({ center: { lat: getCenterFunc.lat(), lng: getCenterFunc.lng() } });
  }, 900);

  const openInspectionModal = () => {
    setIsViewInspectionDialogOpen(true);
  }

  function reloadMap() {
    setKey(new Date().toString());
  }

  const handleReturnToLatestContainer = () => {
    setSelectedContainerId(null); // Null will fetch latest container
  }

  const handleRetryLastCompletedValuation = () => {
    sendClientLog('Info', 'Retry - Fetch Last Completed Valuation.');
    fetchLastCompletedValuation(); //  retry to fetch last completion valuation if one exists, last attempt was an error due to timeout
  }

  const PropertyMap = useMemo(() =>
    <MapWrapper
      zoom={zoom}
      center={center}
      mapViewType={mapViewType}
      onZoomChanged={onZoomChanged}
      onCenterChanged={onCenterChanged}
      gestureHandling={gestureHandling}
    >
      <PropertyUWMap />
    </MapWrapper>
    , [zoom, center, mapViewType, gestureHandling]);

  const errorBoundaryFallback = (errorInfo) => {
    const error = errorInfo?.error;
    if (error) {
      sendClientLog(
        'Error',
        `Client API - Underwrite Map issue (selectedOpportunityId = ${selectedOpportunity?.id}); ${error.toString()}`
      );
    }

    return (
      <div className={classes.errorWrapper}>
        <div className={classes.errorHeader}>Error displaying the map</div>
        <Button
          className={classes.reloadMapBtn}
          variant="contained"
          color="primary"
          size="small"
          onClick={reloadMap}
        >
          Reload Map
        </Button>
        <Accordion className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <span>View details</span>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Typography variant='body2'>
              {error && error.toString()}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
    )
  }

  return (
    displayMode === Constants.DisplayMode.LAPTOP || displayMode === Constants.DisplayMode.MOBILE
      ? <Paper className={classes.myTasksContainer} elevation={3} square id='propertyUWContainer'>
          <PropertyUWHeader
            containerClass={classes.header}
            inspectionOpen={openInspectionModal}
            opportunities={selectedOpportunityHistory}
          />
          <div
            id='propertyUWContentContainer'
            className={classNames(classes.content, classes.currentUserContent)}
          >
            {viewingReadonlyMode && (
              <UnderwriteWarningBanner 
                style={{ backgroundColor: '#ddd' }}
                message={`Viewing snapshot${snapshotDate ? ` from ${format(new Date(snapshotDate), 'MM/dd/yyyy, h:mm:ss a')}` : ''} in read-only mode.`}
                actionButtonText='Return to latest'
                onActionClick={handleReturnToLatestContainer}
              />
            )
            }
            {isAmherstManaged && (
              <UnderwriteWarningBanner  style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb' }} message='Caution: This property might be Amherst-managed.' />
            )
            }
            <div id='propUWValidationSection'>
              {!viewingReadonlyMode ? <PropertyUWDiscrepantData /> : null}
            </div>
            <div className={classes.flexibleSection} style={{ minHeight: 'min-content' }}>
              <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 523 }}>
                <PropertyUWLastCompleted
                  onActionClick={handleRetryLastCompletedValuation}
                />
                <div style={{ margin: 8, width: 512, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <UnderwritePictures module={Constants.Module.UNDERWRITE} />
                  <Divider style={{ margin: '12px 0 6px' }} />
                  <div className={classes.notesSection}>
                    {selectedOpportunity?.notes && <UnderwriteNotes notes={selectedOpportunity.notes} />}
                    {selectedOpportunity?.notes && selectedOpportunity?.investmentManagementPricingNotes && <Divider style={{ margin: 2 }} />}
                    {selectedOpportunity?.investmentManagementPricingNotes && <PropertyUWInvestmentManagementPricingNotes notes={selectedOpportunity.investmentManagementPricingNotes} />}
                  </div>
                </div>
              </div>
              <div className={classNames(viewingReadonlyMode && classes.grayScale)} style={{ flexGrow: 1, display: 'flex' }}>
                <Paper id='propUWMapSection' className={classes.paperSection} elevation={0} key={key} style={{ flexGrow: 1 }}>
                  <ErrorBoundary fallbackRender={errorBoundaryFallback}>
                    {PropertyMap}
                  </ErrorBoundary>
                </Paper>
              </div>
            </div>
            <Divider style={{ margin: 4 }} />
            <div id='propUWComparableSection'>
              <div className={classNames(viewingReadonlyMode && classes.grayBackground)} style={{ padding: '2px 8px', height: '100%', maxHeight: 500 }}>
                <PropertyUWGrid />
              </div>
            </div>
            <Divider style={{ margin: 6 }} />
            <div id='propUWHistorySection'>
              <PropertyHistoryTabs transactionEvents={selectedOpportunityEventHistory} opportunities={selectedOpportunityHistory} valuations={selectedOpportunityValuationHistory} txnOppID={selectedOpportunity.id} />
            </div>
          </div>
        </Paper>
      : <Paper className={classes.myTasksContainer} elevation={3} square id='propertyUWContainer'>
          <PropertyUWHeader
            containerClass={classes.header}
            inspectionOpen={openInspectionModal}
            opportunities={selectedOpportunityHistory}
          />
          <div
            id='propertyUWContentContainer'
            className={classNames(classes.content, classes.currentUserContent)}
          >
            {viewingReadonlyMode && (
              <UnderwriteWarningBanner 
                style={{ backgroundColor: '#ddd' }}
                message={`Viewing snapshot${snapshotDate ? ` from ${format(new Date(snapshotDate), 'MM/dd/yyyy, h:mm:ss a')}` : ''} in read-only mode.`}
                actionButtonText='Return to latest'
                onActionClick={handleReturnToLatestContainer}
              />
            )
            }
            <div className={classes.flexibleSection} style={{ minHeight: 523 }}>
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <PropertyUWLastCompleted
                  onActionClick={handleRetryLastCompletedValuation}
                />
                <div style={{ margin: 8, width: 512, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <UnderwritePictures module={Constants.Module.UNDERWRITE} />
                  <Divider style={{ margin: '8px 0 4px' }} />
                  <div className={classes.notesSection}>
                    {selectedOpportunity?.notes && <UnderwriteNotes notes={selectedOpportunity.notes} />}
                    {selectedOpportunity?.notes && selectedOpportunity?.investmentManagementPricingNotes && <Divider style={{ margin: 2 }} />}
                    {selectedOpportunity?.investmentManagementPricingNotes && <PropertyUWInvestmentManagementPricingNotes notes={selectedOpportunity.investmentManagementPricingNotes} />}
                  </div>
                </div>
              </div>
              <div className={classNames(viewingReadonlyMode && classes.grayScale)} style={{ flexGrow: 1, display: 'flex' }}>
                <Paper id='propUWMapSection' className={classes.paperSection} elevation={0} key={key} style={{ flexGrow: 1 }}>
                  <ErrorBoundary fallbackRender={errorBoundaryFallback}>
                    {PropertyMap}
                  </ErrorBoundary>
                </Paper>
              </div>
            </div>
            <Divider style={{ margin: '0px 6px 2px', zIndex: 1 }} />
            <div style={{ display: 'flex', flexGrow: 1, overflowY: 'auto', minHeight: 410 }}>
              <div id='propUWComparableSection' style={{ width: '100%', marginTop: -12 }}>
                <div className={classNames(viewingReadonlyMode && classes.grayBackground)} style={{ padding: '2px 8px', height: '100%' }}>
                  <PropertyUWGrid />
                </div>
              </div>
            </div>
            <Divider style={{ margin: 6 }} />
            <div id='propUWHistorySection'>
              <PropertyHistoryTabs transactionEvents={selectedOpportunityEventHistory} opportunities={selectedOpportunityHistory} valuations={selectedOpportunityValuationHistory} txnOppID={selectedOpportunity.id} />
            </div>
          </div>
        </Paper>
  )
};

const mapStateToProps = ({ appState, propertyUWState }) => {
  const { selectedContainerId, selectedContainer, containers } = propertyUWState;
  const snapshotDate = selectedContainerId != null && selectedContainer?.viewingReadonlyMode
    ? containers?.find(container => container.id === selectedContainerId)?.completedOn
    : null;
  return {
    displayMode: appState.displayMode,
    selectedOpportunity: propertyUWState.propertyUWClientTxnOpp,
    viewingReadonlyMode: selectedContainerId != null && selectedContainer?.viewingReadonlyMode,
    snapshotDate,
    zoom: propertyUWState.propertyUWMapState.zoom,
    center: propertyUWState.propertyUWMapState.center,
    gestureHandling: propertyUWState.propertyUWMapState.gestureHandling,
    mapViewType: propertyUWState.propertyUWMapState.mapViewType,
    isAmherstManaged: propertyUWState.selectedOpportunityAmherstIndicative?.isAmherstManaged,
    selectedOpportunityHistory: propertyUWState.selectedOpportunityHistory,
    selectedOpportunityEventHistory: propertyUWState.selectedOpportunityEventHistory,
    selectedOpportunityValuationHistory: propertyUWState.selectedOpportunityValuationHistory
  }
};
const mapDispatchToProps = {
  setPropertyUWMapState,
  setSelectedContainerId,
  fetchLastCompletedValuation
};

const styles = theme => ({
  myTasksContainer: {
    // display: 'grid',
    display: 'flex',
    flexDirection: 'column',
    gridTemplateAreas: `
      'header'
      'content'
      `,
    gridTemplateColumns: '1fr',
    gridTemplateRows: '80px 1fr',
    // backgroundColor: 'transparent'
    overflow: 'auto'
  },
  header: {
    gridArea: 'header',
    width: '100%',
  },
  content: {
    gridArea: 'content',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    padding: '0 8px'
  },
  currentUserContent: {
    boxShadow: '0px 0px 20px 0 transparent',
  },
  flexibleSection: {
    display: 'flex',
    flexWrap: 'wrap',
    // minHeight: 'fit-content',
    // flexGrow: 1
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  paperSection: {
    width: '580px',
    minHeight: 507,
    margin: 8,
    // padding: 16,
    borderRadius: 8,
    overflow: 'hidden'
  },
  notesSection: {
    height: '100%',
    // maxHeight: 143,
    overflow: 'auto'
  },
  errorWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    height: '100%',
  },
  errorHeader: {
    color: '#e63946',
    fontWeight: 700,
    fontSize: '1rem',
  },
  reloadMapBtn: {
    marginTop: '1rem',
    fontSize: '0.8rem',
  },
  accordion: {
    width: '50%',
    boxShadow: 'none',
    position: 'static',
    textAlign: 'center',
    margin: '0 !important',
  },
  accordionSummary: {
    display: 'inline-flex',
    marginLeft: '0.5rem',
  },
  accordionDetails: {
    backgroundColor: '#ffe5ec',
    borderRadius: '0.5rem',
    textAlign: 'start'
  },
  grayScale: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff',
    borderRadius: 8
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  }
})
export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PropertyUWContent);
